import { useState, useRef, useCallback } from "react";

// ========== UploadBox Component ==========
export function UploadBox({ label, icon, multiple, onFiles, onInvalid, allowed, disabled, limitHint }) {
  const [dragging, setDragging] = useState(false);
  const ref = useRef();

  const handle = useCallback(newFiles => {
    const valid = [];
    const invalid = [];
    Array.from(newFiles).forEach(file => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (allowed.includes(ext)) valid.push(file);
      else invalid.push(file.name);
    });
    if (invalid.length) {
      onInvalid(`Invalid file type${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}. Please upload ${allowed.join(', ')} files only.`);
    }
    if (valid.length) onFiles(valid);
  }, [onFiles, onInvalid, allowed]);

  return (
    <div className="upload-section">
      <p className="upload-title">{label}</p>
      <div
        onDrop={event => { event.preventDefault(); setDragging(false); handle(event.dataTransfer.files); }}
        onDragOver={event => { event.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onClick={event => {
          if (disabled) return;
          if (event.target === ref.current) return;
          ref.current.click();
        }}
        className={`upload-box ${dragging ? "dragging" : ""} ${disabled ? "disabled" : ""}`}
      >
        <div className="upload-icon">{icon}</div>
        <p>Drop here or <span>browse</span></p>
        <p className="upload-hint">{allowed.map(ext => ext.slice(1).toUpperCase()).join(', ')}</p>
        {limitHint && <p className="upload-hint" style={{ marginTop: '6px', color: '#6b7280', fontWeight: '500', background: '#fef3c7', borderRadius: '4px', padding: '2px 6px' }}>{limitHint}</p>}
        <input
          ref={ref}
          type="file"
          multiple={multiple}
          accept="*"
          style={{ display: "none" }}
          onChange={event => { handle(event.target.files); event.target.value = ""; }}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

// ========== StatusCard Component ==========
export function StatusCard({ steps, currentStep }) {
  return (
    <div className="status-card">
      <h3>Processing...</h3>
      <div className="step-list">
        {steps.map((step, index) => {
          const done = index < currentStep;
          const active = index === currentStep;
          return (
            <div key={step} className="step">
              <div className={`step-indicator ${done ? "done" : active ? "active" : "pending"}`}>
                {done ? "✓" : index + 1}
              </div>
              <span className={`step-label ${done ? "done" : active ? "active" : "pending"}`}>
                {step}
              </span>
              {active && (
                <div className="loading-dots">
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ========== SuccessCard Component ==========
export function SuccessCard({ downloadUrl, downloadName, onReset, onDownloadError }) {
  const handleDownload = async () => {
    try {
      const res = await fetch(downloadUrl);

      if (!res.ok) {
        if (res.status === 404) {
          onDownloadError('Your report has expired or was not found. Please re-run to generate a new report.');
        } else if (res.status === 400) {
          onDownloadError('Report is not ready yet. Please wait for processing to complete.');
        } else if (res.status >= 500) {
          onDownloadError('Server error occurred. Please try again or contact your administrator.');
        } else {
          const errorData = await res.json().catch(() => null);
          const detail = errorData?.detail || `Error downloading file (${res.status})`;
          onDownloadError(detail);
        }
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.name === 'TypeError' || err.message?.toLowerCase().includes('network')) {
        onDownloadError('Network error. Please check your connection and try again.');
      } else {
        onDownloadError(`Unexpected error: ${err.message || 'Unable to download file'}`);
      }
    }
  };

  return (
    <div className="success-card">
      <div className="success-icon">✅</div>
      <h2>Report Ready!</h2>
      <p>Your report has been generated.</p>
      <button className="download-btn" onClick={handleDownload}>
        ⬇ Download {downloadName}
      </button>
      <button onClick={onReset} className="reset-btn">Start a new run</button>
    </div>
  );
}

// ========== ErrorCard Component ==========
export function ErrorCard({ errorMsg, onRetry }) {
  return (
    <div className="error-card">
      <h3>Something went wrong</h3>
      <p>{errorMsg}</p>
      <button onClick={onRetry} className="retry-btn">Try again</button>
    </div>
  );
}

// ========== Toast Component ==========
export function Toast({ message, onClose }) {
  if (!message) return null;

  return (
    <div className="toast">
      <span>⚠️ {message}</span>
      <button className="toast-close" onClick={onClose}>×</button>
    </div>
  );
}
